import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { QuestionMark, CreditCard, EmojiEvents, Upload } from '@mui/icons-material';
import { Spread } from '../types/spread';
import { spreadsService } from '../services/spreadsService';
import { appointmentService, workingHoursService, type CreateAppointmentData } from '../services/appointmentService';
import { TimeSlot } from '../types/timeSlot';
import { authService } from '../services/authService';
import { SelectChangeEvent } from '@mui/material/Select';
import TimeSlotPicker from '../components/TimeSlotPicker';
import { format, parseISO, isValid, startOfDay } from 'date-fns';
import type { WorkingHours } from '../services/appointmentService';
import { Timestamp } from 'firebase/firestore';
import type { Appointment } from '../types/appointment';
import { errorHandler } from '../utils/errorHandler';
import { storageService } from '../services/storageService';

// Türkçe ay isimleri
const TURKISH_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

// Tarihi Türkçe formatla
const formatDateTurkish = (date: Date): string => {
  const day = date.getDate();
  const month = TURKISH_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

interface BookingFormData {
  name: string;
  phone: string;
  date: Date | null;
  time: string | null;
  spreadId: string;
  notes: string;
}

const IBAN_NUMBER = "TR91 0001 5001 5800 7345 8185 86"; // Gül Gülten - Vakıfbank

const Booking: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const initialSpreadId = urlParams.get('spreadId') || '';
  const urlBookingType = urlParams.get('type');
  const flag = Number(urlParams.get('flag')) || 1;
  
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    phone: '',
    date: null,
    time: null,
    spreadId: initialSpreadId,
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[] | null>(null);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [timeSlotsError, setTimeSlotsError] = useState<string | null>(null);
  const [spreads, setSpreads] = useState<Spread[]>([]);
  const [loadingSpreads, setLoadingSpreads] = useState(true);
  const [spreadsError, setSpreadsError] = useState<string | null>(null);
  const [selectedSpread, setSelectedSpread] = useState<Spread | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [userPoints, setUserPoints] = useState<number>(0);
  const [insufficientPoints, setInsufficientPoints] = useState(false);
  const [bookingType, setBookingType] = useState<'regular' | 'points'>(
    urlBookingType === 'points' ? 'points' : 'regular'
  );
  const [workingHours, setWorkingHours] = useState<WorkingHours>({
    start: '13:00',
    end: '22:00'
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const loadSpreads = async () => {
    try {
      setLoadingSpreads(true);
      setSpreadsError(null);
      const data = await spreadsService.getAll();
      setSpreads(data);
      if (initialSpreadId) {
        const spread = data.find(s => s.id === initialSpreadId);
        setSelectedSpread(spread || null);
      }
    } catch (error) {
      setSpreadsError('Fal seçenekleri yüklenirken bir hata oluştu');
    } finally {
      setLoadingSpreads(false);
    }
  };

  const loadUserPoints = async () => {
    try {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const userProfile = await authService.getUserProfile(currentUser.uid);
        if (userProfile) {
          setUserPoints(userProfile.points || 0);
        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const loadWorkingHours = async () => {
    try {
      const hours = await workingHoursService.getWorkingHours();
      setWorkingHours(hours);
    } catch (error) {
      // Handle error silently
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        await loadSpreads();
        await loadUserPoints();
        await loadWorkingHours();
      } catch (error) {
        // Handle error silently
      }
    };
    init();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      const user = authService.getCurrentUser();
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const userProfile = await authService.getUserProfile(user.uid);
        if (userProfile) {
          setFormData(prev => ({
            ...prev,
            name: userProfile.name || '',
            phone: userProfile.phone || ''
          }));
        }
      } catch (err) {
        // Handle error silently
      }
    };

    const unsubscribe = authService.onAuthStateChange((user) => {
      if (!user) {
        navigate('/login');
      }
    });

    loadUserData();
    
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!formData.date) {
        setTimeSlots(null);
        return;
      }

      try {
        setLoadingTimeSlots(true);
        setTimeSlotsError(null);

        // Tarih geçerliliğini kontrol et
        if (!isValid(formData.date)) {
          throw new Error('Geçersiz tarih');
        }

        // Tarihi yerel saat dilimine göre ayarla
        const localDate = startOfDay(formData.date);

        errorHandler.log('Loading time slots for date:', format(localDate, 'yyyy-MM-dd'));
        const slots = await appointmentService.getAvailableTimeSlots(localDate);
        
        if (!slots || slots.length === 0) {
          setTimeSlotsError('Seçilen tarih için müsait randevu saati bulunmamaktadır.');
          setTimeSlots([]);
          return;
        }
        
        setTimeSlots(slots);
      } catch (err: any) {
        const errorMessage = err?.message || 'Müsait saatler yüklenirken bir hata oluştu';
        errorHandler.handleError(err, 'Error loading time slots in Booking component');
        setTimeSlotsError(errorMessage);
        setTimeSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    loadTimeSlots();
  }, [formData.date]);

  const handleTextChange = (field: keyof BookingFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const spreadId = event.target.value;
    setFormData(prev => ({
      ...prev,
      spreadId
    }));
    const spread = spreads.find(s => s.id === spreadId);
    setSelectedSpread(spread || null);
  };

  const handleTimeSelect = (time: string) => {
    setFormData(prev => ({
      ...prev,
      time
    }));
  };

  const handlePaymentConfirm = async () => {
    if (!receiptFile) {
      setError('Lütfen dekont yükleyiniz.');
      return;
    }

    try {
      setLoading(true);
      // Dekont yükleme işlemi
      const { fileId, downloadUrl } = await appointmentService.uploadReceipt(receiptFile, formData.phone);
      
      // Başarılı olduğunda randevu oluştur
      if (fileId && downloadUrl) {
        console.log('Dekont yüklendi:', { fileId, downloadUrl });
        setError(null);
        // Randevu oluşturma işlemini başlat
        await handleSubmit(undefined, { fileId, downloadUrl });
        setPaymentDialogOpen(false);
      }
    } catch (uploadError) {
      // Dekont yükleme hatası
      if (uploadError instanceof Error) {
        setError(`Dekont yüklenirken bir hata oluştu: ${uploadError.message}`);
      } else {
        setError('Dekont yüklenirken bir hata oluştu. Lütfen tekrar deneyiniz.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent | undefined, receiptInfo?: { fileId: string; downloadUrl: string }) => {
    if (event) {
      event.preventDefault();
    }

    if (!formData.date || !formData.time || !selectedSpread) {
      setError('Lütfen tüm alanları doldurunuz.');
      return;
    }

    // Check if this is a points-based booking
    if (bookingType === 'points') {
      try {
        setLoading(true);

        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          throw new Error('Kullanıcı bulunamadı');
        }

        // Check if user has enough points
        if (userPoints < (selectedSpread.price || 0)) {
          setInsufficientPoints(true);
          setError('Yetersiz puan');
          return;
        }

        const appointmentData: CreateAppointmentData = {
          userId: currentUser.uid,
          name: formData.name,
          email: currentUser.email || '',
          phone: formData.phone,
          date: format(formData.date, 'yyyy-MM-dd'),
          time: formData.time,
          spreadId: selectedSpread.id,
          spreadPoints: selectedSpread.price || 0,
          notes: formData.notes || '',
          status: 'pending',
          paymentStatus: 'waiting_confirmation',
          paymentType: 'points',
          receiptFileId: '',
          receiptUrl: ''
        };

        await appointmentService.create(appointmentData);
        navigate('/profile', { 
          state: { 
            showSuccessMessage: true,
            message: 'Randevu talebiniz alındı. Puanlarınız randevu onaylandığında düşülecektir.'
          } 
        });
      } catch (error) {
        if (error instanceof Error) {
          setError(`Randevu oluşturulurken bir hata oluştu: ${error.message}`);
        } else {
          setError('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Regular payment flow
    if (!receiptInfo) {
      setPaymentDialogOpen(true);
      return;
    }

    try {
      setLoading(true);

      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('Kullanıcı bulunamadı');
      }

      const appointmentData: CreateAppointmentData = {
        userId: currentUser.uid,
        name: formData.name,
        email: currentUser.email || '',
        phone: formData.phone,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        spreadId: selectedSpread.id,
        spreadPoints: selectedSpread.points || 0,
        notes: formData.notes || '',
        status: 'pending',
        paymentStatus: 'waiting_confirmation',
        paymentType: 'regular',
        receiptFileId: receiptInfo.fileId,
        receiptUrl: receiptInfo.downloadUrl
      };

      await appointmentService.create(appointmentData);
      navigate('/profile', { 
        state: { 
          showSuccessMessage: true,
          message: 'Randevu talebiniz alındı. Ödemeniz kontrol edildikten sonra randevunuz onaylanacaktır.'
        } 
      });
    } catch (error) {
      if (error instanceof Error) {
        setError(`Randevu oluşturulurken bir hata oluştu: ${error.message}`);
      } else {
        setError('Randevu oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingSpreads) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (spreadsError) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{spreadsError}</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Container maxWidth="lg" sx={{ 
        px: { xs: 2, sm: 3 },
        overflow: 'hidden'
      }}>
        <Box sx={{ mt: 8, mb: 12 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              align="center"
              className="gradient-text"
              sx={{ 
                mb: 6,
                fontSize: {
                  xs: '2rem',    // Mobile devices
                  sm: '3rem',    // Tablet
                  md: '3.75rem'  // Desktop (default h2 size)
                },
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {bookingType === 'points' ? 'Puan ile Randevu Al' : 'Randevu Al'}
            </Typography>

            {insufficientPoints && (
              <Alert severity="warning" sx={{ mb: 4 }}>
                Bu falı almak için yeterli puanınız bulunmamaktadır. Gerekli puan: {selectedSpread?.price}
              </Alert>
            )}

            {bookingType === 'points' && (
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Chip
                  icon={<EmojiEvents />}
                  label={`Mevcut Puanınız: ${userPoints}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}

            {submitted ? (
              <Alert severity="success" sx={{ mt: 4 }}>
                Randevu talebiniz alındı! Profil sayfanıza yönlendiriliyorsunuz...
              </Alert>
            ) : (
              <Paper 
                elevation={3} 
                sx={{ 
                  p: { xs: 2, sm: 3, md: 4 },
                  mt: 4,
                  mx: 'auto',
                  maxWidth: '100%',
                  overflowX: 'hidden'
                }}
              >
                {selectedSpread && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" gutterBottom>
                      {selectedSpread.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {selectedSpread.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip
                        icon={<QuestionMark />}
                        label={`Soru Hakkı: ${selectedSpread.readingDuration}`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        icon={<CreditCard />}
                        label={`${selectedSpread.price} TL`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Seçtiğiniz Tarot Falı: {selectedSpread.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Soru Hakkı: {selectedSpread.readingDuration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ücret: {selectedSpread.price} TL
                    </Typography>
                    {formData.date && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Randevu Tarihi: {formatDateTurkish(formData.date)}
                      </Typography>
                    )}

                    {formData.time && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Randevu Saati: {formData.time}
                      </Typography>
                    )}
                  </Box>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="spread-select-label">Tarot Falı</InputLabel>
                        <Select
                          labelId="spread-select-label"
                          id="spread-select"
                          value={formData.spreadId}
                          label="Tarot Falı"
                          onChange={handleSelectChange}
                          required
                        >
                          {spreads.map((spread) => (
                            <MenuItem key={spread.id} value={spread.id}>
                              {spread.title} - {spread.readingDuration} - {spread.price} TL
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Adınız Soyadınız"
                        value={formData.name}
                        onChange={handleTextChange('name')}
                        required
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telefon Numaranız"
                        value={formData.phone}
                        onChange={handleTextChange('phone')}
                        required
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Tarih Seçin"
                        value={formData.date}
                        onChange={(newValue) => {
                          setFormData(prev => ({
                            ...prev,
                            date: newValue,
                            time: null // Reset time when date changes
                          }));
                        }}
                        disablePast
                        format="dd MMMM yyyy"
                        sx={{ width: '100%' }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TimeSlotPicker
                        timeSlots={timeSlots}
                        selectedTime={formData.time || null}
                        onTimeSelect={(time: string) => handleTimeSelect(time)}
                        loading={loadingTimeSlots}
                        error={timeSlotsError?.toString()}
                        isBookingView={true}
                        selectedDate={formData.date}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Sorular ve Notlar"
                        value={formData.notes}
                        onChange={handleTextChange('notes')}
                        multiline
                        rows={4}
                      />
                    </Grid>

                    {error && (
                      <Grid item xs={12}>
                        <Alert severity="error">{error}</Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={loading}
                        sx={{
                          mt: 2,
                          py: 2,
                          borderRadius: '25px',
                          maxWidth: '100%',
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Randevu Al'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            )}
          </motion.div>
        </Box>

        {/* Ödeme Dialog */}
        <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Ödeme Bilgileri</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              <Typography component="div" variant="h6" gutterBottom>
                Ödeme Tutarı: {selectedSpread?.price} TL
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Lütfen aşağıdaki hesap bilgilerine ödemeyi yaptıktan sonra dekontunuzu yükleyiniz.
              </Alert>

              <Box sx={{ 
                bgcolor: 'background.paper', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mb: 2
              }}>
                <Typography component="div" variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Hesap Bilgileri:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Ad Soyad: Gül Gülten
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Banka: Vakıfbank
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  letterSpacing: '1px'
                }}>
                  {IBAN_NUMBER}
                </Typography>
                <Button 
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(IBAN_NUMBER);
                  }}
                  sx={{ mt: 1 }}
                >
                  IBAN'ı Kopyala
                </Button>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Dekont Yükleme
                </Typography>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="receipt-upload"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Dosya yükleme işlemi burada yapılacak
                      setReceiptFile(file);
                    }
                  }}
                />
                <label htmlFor="receipt-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<Upload />}
                  >
                    Dekont Seç
                  </Button>
                </label>
                {receiptFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                    {receiptFile.name} seçildi
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                * Dekontu yükledikten sonra "Randevu Talebini Gönder" butonuna tıklayınız. 
                Ödemeniz kontrol edildikten sonra randevunuz onaylanacaktır.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialogOpen(false)} disabled={loading}>İptal</Button>
            <Button 
              onClick={handlePaymentConfirm}
              variant="contained"
              color="primary"
              disabled={!receiptFile || loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {loading ? 'Yükleniyor...' : 'Randevu Talebini Gönder'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default Booking; 